import { SearchV2, AlertV2, ButtonV2 } from '@cimplico/components';
import { useState } from 'react';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useOtherSchedules } from '../../queries/use-other-schedules';
import PriorOtherScheduleCard from './PriorOtherScheduleCard';
import { WorksheetStatusEnum } from '../../types/worksheet';
import { isNullOrUndefined } from '../new-workpaper/utils';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import { RectangleGroupIcon } from '@heroicons/react/24/outline';
import { useFirmContext } from '../../providers/FirmProvider';

function CopyPriorOtherScheduleForm(): JSX.Element {
    const [search, setSearch] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);

    const { priorWorkpaperId } = useWorkpaperContext();
    const priorOtherSchedulesQuery = useOtherSchedules(
        { page: 1, limit: 1000, filterQuery: '', searchQuery: '', sortQuery: '' },
        priorWorkpaperId
    );
    const currentOtherSchedulesQuery = useOtherSchedules({ page: 1, limit: 1000, filterQuery: '', searchQuery: '', sortQuery: '' });
    const { shortId } = useFirmContext();
    const isLoading = priorOtherSchedulesQuery.isLoading || currentOtherSchedulesQuery.isLoading;
    const isFetching = priorOtherSchedulesQuery.isFetching || currentOtherSchedulesQuery.isLoading;
    const isError =
        priorOtherSchedulesQuery.isError ||
        currentOtherSchedulesQuery.isError ||
        !priorOtherSchedulesQuery.isSuccess ||
        !currentOtherSchedulesQuery.isSuccess;

    async function refetchQueries(): Promise<void> {
        await currentOtherSchedulesQuery.refetch();
        await priorOtherSchedulesQuery.refetch();
    }

    if (!isLoading && !isFetching && isError) {
        return (
            <div>
                <span className="mr-4">Unable to load other schedules</span>
                <ButtonV2 type="secondary" label="Try Again" size="sm" onClick={refetchQueries}></ButtonV2>
            </div>
        );
    }

    const currentOtherSchedules = currentOtherSchedulesQuery.data?.otherSchedules ?? [];
    const priorOtherSchedules =
        priorOtherSchedulesQuery.data?.otherSchedules.filter((pos) => pos.status !== WorksheetStatusEnum.EXCLUDED) ?? [];
    const searchFilteredSchedules = priorOtherSchedules.filter((pos) => pos.name.toLowerCase().includes(search.toLowerCase()));

    return priorOtherSchedules.length === 0 ? (
        <div className="mt-4">
            <EmptyStateNew
                icon={<RectangleGroupIcon />}
                title="No Other Schedules in Prior Workpaper"
                titleSize="base"
                helpText="Any other schedules added to the prior workpaper will appear here."
                helpLink={`/${shortId}/workpapers/${priorWorkpaperId}/other-schedules`}
                helpLinkText={`Browse Prior Other Schedules`}
            />
        </div>
    ) : (
        <div
            className="grid gap-4 grid-cols-[repeat(auto-fill,300px)] w-full max-w-[1000px] mt-4 justify-center"
            data-intercom-other-schedules-new-grid
        >
            <div className="col-span-full">
                <SearchV2 placeholder="Search for a prior schedule..." onChange={setSearch} />
            </div>
            {isLoading || isFetching ? (
                <>
                    {[...Array(10).keys()].map((_, i) => {
                        return <div key={i} className="animate-pulse bg-gray-100 h-[125px] rounded"></div>;
                    })}
                </>
            ) : (
                <>
                    {searchFilteredSchedules.map((pos) => {
                        const existingLinkedSchedule = currentOtherSchedules.find((cos) => cos.priorWorksheetId === pos.id);
                        return (
                            <PriorOtherScheduleCard
                                key={pos.id}
                                schedule={pos}
                                disabled={isCreating || !isNullOrUndefined(existingLinkedSchedule)}
                                disabledMessage={
                                    !isNullOrUndefined(existingLinkedSchedule)
                                        ? `This schedule has already been linked to "${existingLinkedSchedule.name}" in this workpaper`
                                        : undefined
                                }
                                onCreating={(): void => setIsCreating(true)}
                                onCreate={(): void => setIsCreating(false)}
                            />
                        );
                    })}
                </>
            )}
        </div>
    );
}

export default CopyPriorOtherScheduleForm;
